import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../../../store/services/auth.service";
import {
  BUDGET_COLUMNS,
  BUDGET_CATEGORY_COLUMNS,
  CHANGE_REQUEST_COLUMNS,
  CLIENTS_COLUMNS,
  COMMUNICATION_PLANS_COLUMNS,
  DECISION_LOG_COLUMNS,
  RUNBOOK_COLUMNS,
  RESHUFFLE_REQUEST_COLUMNS,
  Get_HEALTH_STATUS_COLUMNS,
  INVOICE_REGISTRY_COLUMNS,
  MEETING_REGISTRY,
  PORTFOLIO_COLUMNS,
  PROGRAMS_COLUMNS,
  PROJECT_COLUMNS,
  PURCHASE_ORDERS_COLUMNS,
  RESOURCE_COLUMNS,
  ALLOCATED_RESOURCES_COLUMNS,
  RISK_COLUMNS,
  ROLES_COLUMNS,
  SCOPE_COLUMNS,
  SHIFT_COLUMNS,
  TASK_COLUMNS,
  MILESTONE_COLUMNS,
} from "../../../helpers/columns";

export const columnMappings = {
  Role: ROLES_COLUMNS,
  Resource: RESOURCE_COLUMNS,
  Shift: SHIFT_COLUMNS,
  Client: CLIENTS_COLUMNS,
  "Client-Portfolio": PORTFOLIO_COLUMNS.filter(
    (item) => item.label !== "Client"
  ),
  "Client-Program": PROGRAMS_COLUMNS.filter((item) => item.label !== "Client"),
  "Client-Project": PROJECT_COLUMNS.filter((item) => item.label !== "Client"),
  "Client-Communication Plan": COMMUNICATION_PLANS_COLUMNS.filter(
    (item) => item.label !== "Client"
  ),
  Portfolio: PORTFOLIO_COLUMNS,
  "Portfolio-Program": PROGRAMS_COLUMNS.filter(
    (item) => item.label !== "Portfolio" && item.label !== "Client"
  ),
  "Portfolio-Project": PROJECT_COLUMNS.filter(
    (item) => item.label !== "Portfolio" && item.label !== "Client"
  ),
  "Portfolio-Scope": SCOPE_COLUMNS.filter((item) => item.label !== "Portfolio"),
  "Portfolio-Milestone": MILESTONE_COLUMNS,
  "Portfolio-Change Request": CHANGE_REQUEST_COLUMNS.filter(
    (item) => item.label !== "Portfolio" && item.label !== "Client"
  ),
  "Portfolio-Risk": RISK_COLUMNS.filter((item) => item.label !== "Portfolio"  && item.label !== "Client"),
  "Portfolio-Budget": BUDGET_COLUMNS.filter(
    (item) => item.label !== "Portfolio"
  ),
  "Portfolio-Communication Plan": COMMUNICATION_PLANS_COLUMNS.filter(
    (item) => item.label !== "Portfolio" && item.label !== "Client"
  ),
  Program: PROGRAMS_COLUMNS,
  "Program-Project": PROJECT_COLUMNS.filter((item) => item.label !== "Program" && item.label !== "Portfolio" && item.label !== "Client" ),
  "Program-Scope": SCOPE_COLUMNS.filter((item) => item.label !== "Program"),
  "Program-Milestone": MILESTONE_COLUMNS,
  "Program-Change Request": CHANGE_REQUEST_COLUMNS.filter(
    (item) => item.label !== "Program" && item.label !== "Portfolio" && item.label !== "Client"
  ),
  "Program-Risk": RISK_COLUMNS.filter((item) => item.label !== "Program" && item.label !== "Portfolio" && item.label !== "Client"),
  "Program-Budget": BUDGET_COLUMNS.filter((item) => item.label !== "Program"),
  "Program-Communication Plan": COMMUNICATION_PLANS_COLUMNS.filter(
    (item) => item.label !== "Program" && item.label !== "Portfolio" && item.label !== "Client"
  ),
  Project: PROJECT_COLUMNS,
  "Project-Scope": SCOPE_COLUMNS.filter((item) => item.label !== "Project"),
  "Project-Milestone": MILESTONE_COLUMNS.filter((item) => item.label !== "Project"),
  "Project-Change Request": CHANGE_REQUEST_COLUMNS.filter(
    (item) => item.label !== "Project" && item.label !== "Portfolio" && item.label !== "Client"
  ),
  "Project-Risk": RISK_COLUMNS.filter((item) => item.label !== "Project" && item.label !== "Portfolio" && item.label !== "Client"),
  "Project-Budget": BUDGET_CATEGORY_COLUMNS.filter(
    (item) => item.label !== "Project"
  ),
  "Project-Budget-Category": BUDGET_COLUMNS.filter((item) => item.label !== "Client" && item.label !== "Project"),
  "ProgramBudget": BUDGET_CATEGORY_COLUMNS.filter((item) => item.label !== "Category"),
  "Project-Communication Plan": COMMUNICATION_PLANS_COLUMNS.filter(
    (item) => item.label !== "Project" && item.label !== "Program" && item.label !== "Portfolio" && item.label !== "Client"
  ),
  "Project-Decision": DECISION_LOG_COLUMNS.filter(
    (item) => item.label !== "Project"
  ),
  Scope: SCOPE_COLUMNS,
  Tasks: TASK_COLUMNS,
  "Scope-Tasks": TASK_COLUMNS,
  "Change Request": CHANGE_REQUEST_COLUMNS,
  Risk: RISK_COLUMNS,
  "Severity-Risk": RISK_COLUMNS,
  "Communication Plan": COMMUNICATION_PLANS_COLUMNS,
  Budget: BUDGET_COLUMNS,
  MeetingRegistry: MEETING_REGISTRY,
  "Allocated Resources": ALLOCATED_RESOURCES_COLUMNS,
  HealthStatus: Get_HEALTH_STATUS_COLUMNS(),
  InvoiceRegistry: INVOICE_REGISTRY_COLUMNS,
  PurchaseOrders: PURCHASE_ORDERS_COLUMNS,
  Decision: DECISION_LOG_COLUMNS,
  "Program-Decision": DECISION_LOG_COLUMNS,
  Runbook: RUNBOOK_COLUMNS,
  "Project-Reshuffle-Request": RESHUFFLE_REQUEST_COLUMNS,
  "Reshuffle-Request": RESHUFFLE_REQUEST_COLUMNS,
};

const ColumnConfigContext = createContext();

export const ColumnConfigProvider = ({ children, tableName }) => {
  const [columnConfig, setColumnConfig] = useState([]);
  const [tableLayouts, setTableLayouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTableLayouts = async () => {
    try {
      setIsLoading(true);
      const response = await api.userSettings();
      if (response?.layouts?.length) {
        setTableLayouts(response.layouts);
      } else {
        setTableLayouts([]);
      }
    } catch (error) {
      console.error("Error fetching table layouts:", error);
      setTableLayouts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableColumns = (tableName) => {
    const table = tableLayouts.find(
      (table) => table?.name === tableName && table?.columns?.length
    );

    let columnsConfig = columnMappings[tableName] || [];
    if (!columnsConfig.length) {
      return [];
    }

    if (!table) {
      return columnsConfig.map((item) => ({
        columnId: item.columnId,
        label: item.label,
        customName: item.customName ?? "",
        order: item.order ?? 0,
        isSortable: item.isSortable ?? false,
        isVisible: item.isVisible ?? true,
        accessorKey: item.accessorKey ?? "",
        columnType: item.columnType ?? "",
      }));
    }

    const tableColumns = table.columns
      .map((column) => {
        const columnConfig = columnsConfig.find(
          (item) => item.label === column.name
        );
        if (columnConfig) {
          return {
            columnId: columnConfig.columnId,
            label: column.name,
            customName: column.customName ?? "",
            order: column.order,
            isSortable: columnConfig.isSortable ?? false,
            isVisible: column.isVisible ?? true,
            columnType: columnConfig.columnType ?? "",
            accessorKey: columnConfig.accessorKey ?? "",
          };
        }
        return null;
      })
      .filter(Boolean);

    // Find columns that exist in local config but not in database
    const dbColumnNames = table.columns.map((col) => col.name);
    const newColumns = columnsConfig
      .filter((localColumn) => !dbColumnNames.includes(localColumn.label))
      .map((item) => ({
        columnId: item.columnId,
        label: item.label,
        customName: item.customName ?? "",
        order: item.order ?? 0,
        isSortable: item.isSortable ?? false,
        isVisible: item.isVisible ?? true,
        accessorKey: item.accessorKey ?? "",
        columnType: item.columnType ?? "",
      }));

    // Combine database columns with new local columns
    const allColumns = [...tableColumns, ...newColumns];
    // Sort by order to maintain consistency
    allColumns.sort((a, b) => (a.order || 0) - (b.order || 0));

    return allColumns.length ? allColumns : columnsConfig;

    // return tableColumns.length ? tableColumns : columnsConfig;
  };

  const getColumnConfigs = (tableName) => {
    const DBTableColumns = getTableColumns(tableName);
    return DBTableColumns.length
      ? DBTableColumns
      : columnMappings[tableName] || [];
  };

  const updateColumnConfig = (newColumns) => {
    setColumnConfig(newColumns);
    fetchTableLayouts();
  };

  const onRefreshColumnConfig = async () => {
    try {
      setIsLoading(true);
      const response = await api.userSettings();
      if (response?.layouts?.length) {
        setTableLayouts(response.layouts);
      } else {
        setTableLayouts([]);
      }
    } catch (error) {
      console.error("Error fetching table layouts:", error);
      setTableLayouts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTableLayouts();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const DBTableColumns = getTableColumns(tableName);
      setColumnConfig(
        DBTableColumns.length ? DBTableColumns : columnMappings[tableName] || []
      );
    }
  }, [tableLayouts, tableName, isLoading]);

  return (
    <ColumnConfigContext.Provider
      value={{
        columnConfig,
        updateColumnConfig,
        onRefreshColumnConfig,
        getColumnConfigs,
        tableName,
        isLoading,
        tableColumns: columnMappings[tableName] || [],
      }}
    >
      {children}
    </ColumnConfigContext.Provider>
  );
};

export const useColumnConfig = () => useContext(ColumnConfigContext);
